import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import { Link, useParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import techLiteracyImage from '../assets/tech-literacy.png';
import agTechImage from '../assets/ag-tech.png';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));


const Programs = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { lang } = useParams();
  
  // Function to generate language-aware links
  const getLanguagePath = (path) => {
    const currentLang = lang || 'en';
    return `/${currentLang}${path}`;
  };
  
  // Define healthcareImage as null until the actual image is available
  const healthcareImage = null;

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SectionTitle variant="h3" align="center">
              {t('programs.title')}
            </SectionTitle>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('programs.subtitle')}
            </Typography>
          </Box>
          <Typography variant="body1" align="center" sx={{ mb: 4 }}>
            {t('programs.description')}
          </Typography>
        </Container>
      </Box>

      {/* Tech Education Programs */}
      <Box id="tech-education" sx={{ py: 10 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h4">
                {t('programs.techEducation')}
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('programs.techEducationDesc')}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.digitalSkillsWorkshops')}
                    secondary={t('programs.digitalSkillsDesc')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.codingBootcamps')}
                    secondary={t('programs.codingBootcampsDesc')}
                  />
                </ListItem>
              
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.teacherDevelopment')}
                    secondary={t('programs.teacherDevelopmentDesc')}
                  />
                </ListItem>
              </List>
              
              <Button 
                variant="contained" 
                color="primary" 
                component={Link} 
                to={getLanguagePath('/contact')} 
                sx={{ mt: 2, fontWeight: 700 }}
              >
                {t('programs.getInvolved')}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={techLiteracyImage}
                alt="Technology Literacy Programs"
                sx={{
                  width: '110%',
                  maxWidth: '550px',
                  height: 'auto',
                  borderRadius: 3,
                  boxShadow: theme => `0 12px 24px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.3 : 0.15})`,
                  transform: 'scale(1.05)',
                  marginLeft: { xs: 0, md: '-5%' },
                  transition: 'transform 0.5s ease-in-out, box-shadow 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.07)',
                    boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                  }
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      <Divider />
      
      {/* Agricultural Technology */}
      <Box id="agricultural" sx={{ py: 10 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center" direction={{ xs: 'column-reverse', md: 'row' }}>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={agTechImage}
                alt="Agricultural Technology Programs"
                sx={{
                  width: '110%',
                  maxWidth: '550px',
                  height: 'auto',
                  borderRadius: 3,
                  boxShadow: theme => `0 12px 24px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.3 : 0.15})`,
                  transform: 'scale(1.05)',
                  marginRight: { xs: 0, md: '-5%' },
                  transition: 'transform 0.5s ease-in-out, box-shadow 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.07)',
                    boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h4">
                {t('programs.agricultural')}
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('programs.agriculturalDesc')}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.stappleFoodCrops')}
                    secondary={t('programs.stappleFoodCropsDesc')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.climateSmartAg')}
                    secondary={t('programs.climateSmartAgDesc')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('programs.agriculturalEducation')}
                    secondary={t('programs.agriculturalEducationDesc')}
                  />
                </ListItem>
              </List>
              
              <Button 
                variant="contained" 
                color="primary" 
                component={Link} 
                to={getLanguagePath('/contact')} 
                sx={{ mt: 2, fontWeight: 700 }}
              >
                {t('programs.learnMore')}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Healthcare Accessibility */}
      <Box id="healthcare" sx={{ py: 10 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h4">
                {t('programs.healthcare')}
              </SectionTitle>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {t('programs.healthcareDesc')}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary={t('programs.communityHealthCenters')}
                    secondary={t('programs.communityHealthCentersDesc')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary={t('programs.mobileClinics')}
                    secondary={t('programs.mobileClinicsDesc')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary={t('programs.telehealthInitiatives')}
                    secondary={t('programs.telehealthInitiativesDesc')}
                  />
                </ListItem>
              </List>
              
              <Button 
                variant="contained" 
                color="primary" 
                component={Link} 
                to={getLanguagePath('/contact')} 
                sx={{ mt: 2, fontWeight: 700 }}
              >
                {t('programs.getInvolved')}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {healthcareImage ? (
                <Box
                  component="img"
                  src={healthcareImage}
                  alt="Healthcare Accessibility Programs"
                  sx={{
                    width: '110%',
                    maxWidth: '550px',
                    height: 'auto',
                    borderRadius: 3,
                    boxShadow: theme => `0 12px 24px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.3 : 0.15})`,
                    transform: 'scale(1.05)',
                    marginLeft: { xs: 0, md: '-5%' },
                    transition: 'transform 0.5s ease-in-out, box-shadow 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.07)',
                      boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                    }
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '110%',
                    maxWidth: '550px',
                    height: '300px',
                    borderRadius: 3,
                    boxShadow: theme => `0 12px 24px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.3 : 0.15})`,
                    transform: 'scale(1.05)',
                    marginLeft: { xs: 0, md: '-5%' },
                    transition: 'transform 0.5s ease-in-out, box-shadow 0.5s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.07)',
                      boxShadow: theme => `0 16px 30px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? 0.4 : 0.2})`,
                    },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? '#1B1C1D' : '#3949AB',
                    color: '#fff'
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 100, opacity: 0.8 }} />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Call to Action */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Paper 
            elevation={3} 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              borderRadius: 4,
              backgroundColor: theme.palette.primary.main,
              color: '#fff'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {t('programs.partnerWithUs')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t('programs.partnerDescription')}
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              component={Link}
              to={getLanguagePath('/contact')}
              sx={{ 
                fontWeight: 700,
                py: 1.5, 
                px: 4
              }}
            >
              {t('programs.getInTouch')}
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Programs; 